// Everything the player has earned on this device.
//
// Personal bests, homes lit in total, which days and mechanics have been seen,
// unlocked and placed decorations. Stored as one JSON document in
// `prefs.progress`; small enough that a whole-document write per round is the
// simplest correct thing.

import { prefs } from '../core/storage.js';
import { decorationForDay } from './daily.js';

export const DEFAULT_PROGRESS = Object.freeze({
  bestFreePlay: 0,
  bestByDay: {},
  homesLitTotal: 0,
  roundsPlayed: 0,
  unlockedDecorations: [],
  placedDecorations: {},
  seenMechanics: [],
  seenDays: [],
  seenHowTo: false,
});

/** Read progress, filling defaults for anything unset. */
export function loadProgress() {
  const saved = prefs.progress;
  const p = saved && typeof saved === 'object' ? saved : {};
  return {
    ...DEFAULT_PROGRESS,
    ...p,
    bestByDay: { ...(p.bestByDay ?? {}) },
    unlockedDecorations: [...(p.unlockedDecorations ?? [])],
    placedDecorations: { ...(p.placedDecorations ?? {}) },
    seenMechanics: [...(p.seenMechanics ?? [])],
    seenDays: [...(p.seenDays ?? [])],
  };
}

export function saveProgress(progress) {
  prefs.progress = progress;
  return progress;
}

/**
 * A round finished. Update bests, totals, unlocks.
 *
 * `run` is { mode, seed, day?, practice?, level }. A practice-speed round is
 * recorded for totals and unlocks but never sets a personal best over a
 * normal-speed one.
 *
 * Returns { newBest, unlocked } where `unlocked` is the decoration id newly
 * earned this round, or null.
 */
export function recordResult(progress, result, run) {
  const p = progress;
  let newBest = false;
  let unlocked = null;
  const score = Number(result?.score) || 0;
  const practice = !!run?.practice;

  p.roundsPlayed = (p.roundsPlayed || 0) + 1;
  p.homesLitTotal = (p.homesLitTotal || 0) + (Number(result?.homesLit) || 0);

  for (const m of run?.level?.mechanics ?? []) {
    if (!p.seenMechanics.includes(m)) p.seenMechanics.push(m);
  }

  if (run?.mode === 'day' && Number.isFinite(Number(run.day))) {
    const day = Number(run.day);
    const key = String(day);
    const prev = Number(p.bestByDay[key]) || 0;
    if (!practice && score > prev) { p.bestByDay[key] = score; newBest = prev > 0 || score > 0; }
    if (!p.seenDays.includes(day)) p.seenDays.push(day);
    const deco = decorationForDay(day);
    const id = typeof deco === 'string' ? deco : deco?.id;
    if (id && !p.unlockedDecorations.includes(id)) {
      p.unlockedDecorations.push(id);
      unlocked = id;
    }
  } else if (run?.mode !== 'challenge') {
    const prev = Number(p.bestFreePlay) || 0;
    if (!practice && score > prev) { p.bestFreePlay = score; newBest = prev > 0 || score > 0; }
  } else {
    const prev = Number(p.bestFreePlay) || 0;
    if (!practice && score > prev) { p.bestFreePlay = score; newBest = prev > 0; }
  }

  saveProgress(p);
  return { newBest, unlocked };
}

/** Place a decoration in a slot, or remove it (`id` null). */
export function placeDecoration(progress, slot, id) {
  const placed = { ...progress.placedDecorations };
  // A decoration lives in one slot at a time.
  for (const k of Object.keys(placed)) if (placed[k] === id) delete placed[k];
  if (id) placed[String(slot)] = id; else delete placed[String(slot)];
  progress.placedDecorations = placed;
  saveProgress(progress);
  return placed;
}

/** The highest day the player has finished, or 0. */
export function maxSeenDay(progress) {
  return (progress?.seenDays ?? []).reduce((m, d) => Math.max(m, Number(d) || 0), 0);
}
